import { z } from "zod";
import { ActionBundleSchema, ClinicalActionKindSchema, ClinicalActionSchema, ValidationIssueSchema, } from "./actions.js";
import { DraftedLabOrderSchema } from "./encounter-documentation.js";
import { ManualOrderMarkerSchema, refineManualOrderRunKey } from "./manual-order-marker.js";
import { ModelRunDescriptorSchema } from "./model-governance.js";
import { ImagingOrderDestinationSelectionSchema, ImagingOrderPrerequisiteSchema, StructuredImagingOrderStudySchema, } from "./orders.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema } from "./primitives.js";
import { ReferralEnclosureSelectionSchema, ReferralManualDestinationSchema } from "./referral.js";
import { AgentExecutionModeSchema, RolloutPolicyReferenceSchema } from "./rollout-governance.js";
export const PurposeOfUseSchema = z.enum(["treatment", "payment", "operations"]);
export const ProposedEncounterLabOrderSchema = DraftedLabOrderSchema.extend({
    summary: z.string().trim().min(1).max(500),
    rationale: z.string().trim().min(1).max(2_000),
    clinicalInformation: z.string().trim().min(1).max(4_000),
    /**
     * A "+" order only: its own id and the exact form it was picked on. The run must use the
     * order's own key (AgentRunRequestSchema). Absent on every plan proposal, so plan request
     * hashes are unchanged.
     */
    manualOrder: ManualOrderMarkerSchema.optional(),
}).strict();
/** Requested studies may remain incomplete while the clinician edits the preview. */
export const ProposedEncounterImagingOrderSchema = z
    .object({
    destination: ImagingOrderDestinationSelectionSchema.optional(),
    studies: z
        .array(StructuredImagingOrderStudySchema.omit({ source: true }))
        .min(1)
        .max(20),
    diagnosisCodes: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
    summary: z.string().trim().min(1).max(500),
    rationale: z.string().trim().min(1).max(2_000),
    clinicalQuestion: z.string().trim().min(1).max(4_000),
    /**
     * What the ordering physician confirmed before the study, such as kidney function reviewed
     * for IV contrast. Absent means nothing was confirmed.
     */
    prerequisites: z.array(ImagingOrderPrerequisiteSchema).max(10).optional(),
    /** A "+" order only, as on a lab proposal. Absent on every plan proposal. */
    manualOrder: ManualOrderMarkerSchema.optional(),
})
    .strict();
export const ProposedEncounterReferralSchema = z
    .object({
    specialtyCode: z.string().trim().min(1).max(80).optional(),
    summary: z.string().trim().min(1).max(500),
    referralQuestion: z.string().trim().min(1).max(4_000),
    clinicalRationale: z.string().trim().min(1).max(8_000),
    urgency: z.enum(["routine", "urgent", "emergent"]).default("routine"),
    diagnosisCodes: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
    /**
     * Manual-print clinics only: whom the physician addressed the printed
     * letter to. Ignored (refused by the drafter) under automatic routing.
     */
    manualDestination: ReferralManualDestinationSchema.optional(),
    /**
     * Chart documents the physician ticked to enclose behind the letter
     * (REF-3). The drafter resolves them against the patient's current
     * enclosure candidates into exact, approval-bound attachment bindings.
     */
    enclosures: z.array(ReferralEnclosureSelectionSchema).max(50).optional(),
    /**
     * The "relevant history" bullets the drafting model traced to the record or
     * the physician edited (REF-2). When present they replace the note-built
     * history verbatim (an empty list leaves none) and become part of the exact
     * approved content.
     */
    relevantHistory: z.array(z.string().trim().min(1).max(2_000)).max(100).optional(),
})
    .strict();
export const AgentRunRequestSchema = z
    .object({
    encounterId: IdSchema,
    expectedEncounterVersion: z.number().int().positive(),
    intent: z.enum(["synthesize_plan", "draft_referral", "draft_orders", "draft_discharge"]),
    purposeOfUse: PurposeOfUseSchema,
    idempotencyKey: IdempotencyKeySchema,
    orderKind: z.enum(["laboratory", "imaging"]).optional(),
    proposedLabOrder: ProposedEncounterLabOrderSchema.optional(),
    proposedImagingOrder: ProposedEncounterImagingOrderSchema.refine((proposal) => proposal.studies.every((study) => study.contrast !== "unknown" && study.laterality !== "unknown"), "Review contrast and laterality before preparing the requested study").optional(),
    proposedReferral: ProposedEncounterReferralSchema.optional(),
    requestedRoute: z.enum(["auto", "fast", "deep"]).optional(),
})
    .strict()
    .superRefine((request, context) => {
    const imagingDraft = request.intent === "draft_orders" && request.orderKind === "imaging";
    const laboratoryDraft = request.intent === "draft_orders" && request.orderKind === "laboratory";
    if (!imagingDraft && request.proposedImagingOrder !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["proposedImagingOrder"],
            message: "An imaging proposal is allowed only for an imaging-order draft",
        });
    }
    if (!laboratoryDraft && request.proposedLabOrder !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["proposedLabOrder"],
            message: "A structured lab proposal is allowed only for a laboratory-order draft",
        });
    }
    if (!imagingDraft && request.proposedImagingOrder !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["proposedImagingOrder"],
            message: "A structured imaging proposal is allowed only for an imaging-order draft",
        });
    }
    if (request.intent !== "draft_referral" && request.proposedReferral !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["proposedReferral"],
            message: "A structured referral proposal is allowed only for a referral draft",
        });
    }
    // A "+" order runs only under its own key, and its key only with its marker. Each marked
    // proposal is checked, so two proposals can never share one order's key.
    const markers = [
        request.proposedLabOrder?.manualOrder,
        request.proposedImagingOrder?.manualOrder,
    ].filter((marker) => marker !== undefined);
    if (markers.length === 0) {
        refineManualOrderRunKey(undefined, request.idempotencyKey, context);
    }
    for (const marker of markers) {
        refineManualOrderRunKey(marker, request.idempotencyKey, context);
    }
});
const eventBase = {
    runId: IdSchema,
    sequence: z.number().int().nonnegative(),
    occurredAt: IsoDateTimeSchema,
};
export const AgentRunEventSchema = z.discriminatedUnion("type", [
    z.object({ ...eventBase, type: z.literal("started") }),
    z.object({
        ...eventBase,
        type: z.literal("routing_selected"),
        routing: ModelRunDescriptorSchema,
    }),
    z.object({ ...eventBase, type: z.literal("retrieving") }),
    z.object({
        ...eventBase,
        type: z.literal("context_ready"),
        encounterId: IdSchema,
        encounterVersion: z.number().int().positive(),
        sourceCount: z.number().int().nonnegative(),
    }),
    z.object({
        ...eventBase,
        type: z.literal("action_draft_started"),
        bundleId: IdSchema,
        bundleVersion: z.number().int().positive(),
        actionIndex: z.number().int().nonnegative(),
        kind: ClinicalActionKindSchema,
    }),
    z.object({
        ...eventBase,
        type: z.literal("action_ready"),
        bundleId: IdSchema,
        bundleVersion: z.number().int().positive(),
        action: ClinicalActionSchema,
    }),
    z.object({ ...eventBase, type: z.literal("draft_ready"), bundle: ActionBundleSchema }),
    z.object({
        ...eventBase,
        type: z.literal("validation_started"),
        bundleId: IdSchema,
        bundleVersion: z.number().int().positive(),
        actionId: IdSchema.optional(),
    }),
    z.object({
        ...eventBase,
        type: z.literal("validation_completed"),
        bundleId: IdSchema,
        bundleVersion: z.number().int().positive(),
        actionId: IdSchema.optional(),
        issues: z.array(ValidationIssueSchema),
    }),
    z.object({
        ...eventBase,
        type: z.literal("validation_warning"),
        issues: z.array(ValidationIssueSchema),
    }),
    z.object({
        ...eventBase,
        type: z.literal("shadow_completed"),
        policy: RolloutPolicyReferenceSchema,
    }),
    z.object({
        ...eventBase,
        type: z.literal("review_available"),
        bundleId: IdSchema,
        policy: RolloutPolicyReferenceSchema,
    }),
    z.object({
        ...eventBase,
        type: z.literal("approval_required"),
        bundleId: IdSchema,
        policy: RolloutPolicyReferenceSchema,
    }),
    z.object({ ...eventBase, type: z.literal("completed"), bundleId: IdSchema }),
    z.object({
        ...eventBase,
        type: z.literal("cancelled"),
        code: z.string().min(1).max(100),
    }),
    z.object({
        ...eventBase,
        type: z.literal("failed"),
        code: z.string().min(1).max(100),
        // Static, PHI-free policy message from a DomainError (the same text the HTTP
        // error envelope would carry), so the client can show the exact safe reason.
        reason: z.string().trim().min(1).max(500).optional(),
    }),
]);
export const AgentRunAcceptedSchema = z.object({
    runId: IdSchema,
    eventsUrl: z.string().min(1),
    routing: ModelRunDescriptorSchema,
    mode: AgentExecutionModeSchema,
    rolloutPolicy: RolloutPolicyReferenceSchema,
});
//# sourceMappingURL=agent.js.map